import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FileText, Building2, CheckCircle2, Clock, XCircle, Award, Sparkles, User, Key } from 'lucide-react';

export default function ApplicantDashboard() {
    const { user, checkAuth } = useAuth();

    useEffect(() => {
        checkAuth();
    }, []);

    const applicant = user;
    const visits = applicant?.company_visits || [];

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
            
            {/* Header Card */}
            <div className="glass-card rounded-2xl p-6 sm:p-8 border border-slate-800 bg-gradient-to-r from-emerald-950/40 via-slate-950 to-indigo-950/40 shadow-2xl">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                            Applicant Portal
                        </span>
                        <h1 className="text-3xl font-extrabold mt-2 text-white">{applicant?.name || 'Job Applicant'}</h1>
                        <p className="text-xs text-slate-400 mt-1">📱 {applicant?.phone_number} | ✉️ {applicant?.email}</p>
                    </div>

                    <div className="p-3 bg-slate-900/90 rounded-xl border border-slate-800 text-center">
                        <span className="text-[10px] text-slate-400 uppercase font-bold block">Registration Pass</span>
                        <span className="text-lg font-black text-indigo-400">#{applicant?.id || 0}</span>
                    </div>
                </div>
            </div>

            {/* Profile Overview */}
            <div className="glass-card rounded-2xl p-6 border border-slate-800 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                <div>
                    <span className="text-slate-500 font-bold uppercase text-[10px]">Education</span>
                    <div className="font-bold text-white mt-1">{applicant?.highest_education || 'N/A'}</div>
                </div>
                <div>
                    <span className="text-slate-500 font-bold uppercase text-[10px]">Experience Type</span>
                    <div className="font-bold text-amber-300 mt-1">{applicant?.experience_type || 'Fresher'} ({applicant?.years_of_experience || '0 Yrs'})</div>
                </div>
                <div>
                    <span className="text-slate-500 font-bold uppercase text-[10px]">Location</span>
                    <div className="font-bold text-slate-200 mt-1">{applicant?.city || 'N/A'}</div>
                </div>
                <div>
                    <span className="text-slate-500 font-bold uppercase text-[10px]">Preferred Sectors</span>
                    <div className="font-bold text-purple-300 mt-1 truncate">{applicant?.job_applying_for || 'General'}</div>
                </div>
            </div>

            {/* Assigned Stall Visits & Live Status Tracker */}
            <div className="glass-card rounded-2xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                        <Sparkles className="w-5 h-5 text-amber-400" />
                        <h2 className="text-xl font-bold text-white">Your Assigned Stall Visits & Interview Status</h2>
                    </div>
                    <span className="text-xs text-slate-400">Visit in sequence (Order 1, 2, 3)</span>
                </div>

                <div className="space-y-4">
                    {visits.map((v, i) => (
                        <div key={v.id} className="p-5 bg-slate-900/90 rounded-2xl border border-slate-800 hover:border-slate-700 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="space-y-1">
                                <div className="flex items-center space-x-2">
                                    <span className="text-xs font-black text-indigo-400 bg-indigo-500/20 px-2.5 py-1 rounded-md border border-indigo-500/30">
                                        STALL #{v.visit_order || (i+1)}
                                    </span>
                                    <span className="font-extrabold text-lg text-white">{v.company?.company_name}</span>
                                </div>
                                <div className="text-xs text-slate-400">📍 Location: {v.company?.office_location || 'Main Hall'} • Sector: {v.company?.sector || 'General'}</div>
                                {v.remarks && (
                                    <div className="text-xs text-amber-300/90 italic bg-amber-950/30 p-2 rounded-lg border border-amber-500/20 mt-2">
                                        💬 HR Feedback: "{v.remarks}"
                                    </div>
                                )}
                            </div>

                            <div className="text-right shrink-0">
                                <span className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wide inline-flex items-center space-x-1.5 ${
                                    v.status === 'selected' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 glow-emerald' :
                                    v.status === 'rejected' ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40' :
                                    v.status === 'visited' ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40' :
                                    'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                }`}>
                                    {v.status === 'selected' && <Award className="w-4 h-4 text-emerald-400" />}
                                    {v.status === 'visited' && <Clock className="w-4 h-4 text-indigo-400" />}
                                    <span>{v.status}</span>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
